import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  TableCell,
  Paper,
} from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/MedicalExpensePdf";
import CalculationSummary from "../utils/CalculationSummary";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import ScheduleContainer from "../utils/ScheduleContainer";
import SectionBox from "../utils/SectionBox";

const columnSettings = [
  { field_type: "string", field: "reference", align: "center" },
];

const MedicalExpenseSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState("Medical Expense");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [employeeRemunerationRows, setEmployeeRemunerationRows] = useState([]);
  const [medicalExpenseRows, setMedicalExpenseRows] = useState([]);
  const [totalEmployeeRemuneration, setTotalEmployeeRemuneration] = useState(0);
  const [totalMedicalExpense, setTotalMedicalExpense] = useState(0);
  const [disallowableExpenses, setDisallowableExpenses] = useState(0);
  const [expenseCappedAt, setExpenseCappedAt] = useState(1);
  const [expenseCappedAmount, setExpenseCappedAmount] = useState(0);

  const handleSave = () => {
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      employeeRemunerationRows: employeeRemunerationRows,
      medicalExpenseRows: medicalExpenseRows,
      cappedAt: expenseCappedAt,
      totalAdjustment: disallowableExpenses,
      doc: JSON.stringify({
        employeeRemunerationRows,
        medicalExpenseRows,
        totalEmployeeRemuneration,
        totalMedicalExpense,
        disallowableExpenses,
        expenseCappedAt,
        expenseCappedAmount,
        notes,
        reviewNotes,
      }),
    });
  };

  const calculateDisallowableExpenses = (cappedAmount, manual_me) => {
    let total = totalMedicalExpense;
    if (manual_me) total = manual_me;

    let disallowableExpenses = total - cappedAmount;

    if (disallowableExpenses < 0) disallowableExpenses = 0;
    setDisallowableExpenses(disallowableExpenses);
  };

  const calculateCappedAmount = (value, manual_er) => {
    let total = totalEmployeeRemuneration;
    if (manual_er) total = manual_er;
    return total * (value / 100);
  };

  const handleCappedAt = (value) => {
    if (value === "" || (value >= 0 && value <= 100)) {
      setExpenseCappedAt(value);

      let cappedAmount = calculateCappedAmount(value);
      setExpenseCappedAmount(cappedAmount);

      calculateDisallowableExpenses(cappedAmount);
    }
  };

  const handleInputChange = (value, { index, field, field_type, from }) => {
    const updatedRows = [
      ...(from === "employeeRemunerationRows"
        ? employeeRemunerationRows
        : medicalExpenseRows),
    ];

    updatedRows[index][field] = value;

    if (from === "employeeRemunerationRows") {
      setEmployeeRemunerationRows(updatedRows);
    } else {
      setMedicalExpenseRows(updatedRows);
    }
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setEmployeeRemunerationRows(scheduleDetails.employeeRemunerationRows);
      setMedicalExpenseRows(scheduleDetails.medicalExpenseRows);

      //calculation
      let totalEmpRem = CommonCalculator.arraySum(
        scheduleDetails.employeeRemunerationRows
      );
      setTotalEmployeeRemuneration(totalEmpRem);
      let totalME = CommonCalculator.arraySum(
        scheduleDetails.medicalExpenseRows
      );
      setTotalMedicalExpense(totalME);

      let cappedAt = scheduleDetails.cappedAt || expenseCappedAt;
      setExpenseCappedAt(cappedAt);
      const cappedAmount = calculateCappedAmount(cappedAt, totalEmpRem);
      setExpenseCappedAmount(cappedAmount);
      calculateDisallowableExpenses(cappedAmount, totalME);
    }
  }, []);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          handleSave,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          employeeRemunerationRows={employeeRemunerationRows}
          medicalExpenseRows={medicalExpenseRows}
          totalEmployeeRemuneration={totalEmployeeRemuneration}
          totalMedicalExpense={totalMedicalExpense}
          disallowableExpenses={disallowableExpenses}
          expenseCappedAt={expenseCappedAt}
          expenseCappedAmount={expenseCappedAmount}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schedule.name}
        taxAdjustment={"Added Back"}
        allowedToEditSchName
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox title='Employee Remuneration'>
              <CommonTable
                rows={employeeRemunerationRows}
                columns={[
                  {
                    key: "name",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, _) => (
                      <TextFieldForViewOnly
                        value={row.description}
                        sx={{ textAlign: "left" }}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, _) => (
                      <TextFieldForViewOnly value={formatAmount(row.amount)} />
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleInputChange}
                        reference={{
                          index,
                          field: columnSettings[0].field,
                          field_type: columnSettings[0].field_type,
                          from: "employeeRemunerationRows",
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || ""} />
                    ),
                  },
                ]}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Employee Remuneration:
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(totalEmployeeRemuneration)}
                </TableCell>
                <TableCell colSpan={2} />
              </CommonTable>
            </SectionBox>

            <Divider sx={{ marginY: 4 }} />
            <SectionBox title='Medical Expenses'>
              <CommonTable
                rows={medicalExpenseRows}
                columns={[
                  {
                    key: "name",
                    label: "Description",
                    headerAlign: "left",
                    render: (row, _) => (
                      <TextFieldForViewOnly
                        value={row.description}
                        sx={{ textAlign: "left" }}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, _) => (
                      <TextFieldForViewOnly value={formatAmount(row.amount)} />
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleInputChange}
                        reference={{
                          index,
                          field: columnSettings[0].field,
                          field_type: columnSettings[0].field_type,
                          from: "medicalExpenseRows",
                        }}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly value={row.source?.sch_no || ""} />
                    ),
                  },
                ]}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Total Medical Expenses:
                </TableCell>
                <TableCell align='right'>
                  {formatAmount(totalMedicalExpense)}
                </TableCell>
                <TableCell colSpan={2} />
              </CommonTable>
            </SectionBox>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  fontSize: { xs: "1rem", md: "1.15rem" },
                }}
              >
                Medical expense capped at&nbsp;
                {canEditSchedule ? (
                  <TextField
                    type='number'
                    value={expenseCappedAt}
                    onChange={(e) => handleCappedAt(e.target.value)}
                    InputProps={{
                      inputProps: { min: 0, max: 100 },
                      disableUnderline: true,
                    }}
                    sx={{
                      mx: 1,
                      width: 70,
                      "& input": {
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: "1.1em",
                        color: "primary.main",
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        border: "1px solid",
                        borderColor: "divider",
                      },
                    }}
                    variant='standard'
                  />
                ) : (
                  expenseCappedAt
                )}{" "}
                %
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  fontSize: { xs: "1rem", md: "1.15rem" },
                }}
              >
                ({formatAmount(Math.abs(expenseCappedAmount))})
              </Typography>
            </Paper>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      {/* Disallowable medical expenses */}
      <CalculationSummary
        steps={[
          {
            section: "Total Medical Expenses",
            amount: formatAmount(totalMedicalExpense),
            tooltip:
              "The total amount of medical expenses incurred for employees. This is the starting point for calculating disallowable expenses.",
          },
          {
            section: "Capped Medical Expense Amount",
            amount: `(${formatAmount(totalEmployeeRemuneration)} × ${
              expenseCappedAt || 0
            }%) = (${formatAmount(expenseCappedAmount)})`,
            tooltip: `The maximum allowable medical expense amount, calculated as ${
              expenseCappedAt || 0
            }% of total employee remuneration.`,
          },
        ]}
        total={{
          title: "Disallowable Medical Expenses",
          amount: formatAmount(disallowableExpenses),
          tooltip: `Formula: Total Medical Expenses - (Total Employee Remuneration × ${
            expenseCappedAt || 0
          }%). This represents the portion of medical expenses that exceeds the capped amount and may be disallowed for tax purposes.`,
        }}
      />
      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default MedicalExpenseSchedule;
